import { useEffect, useState } from "react";
import { Navigate, Routes, Route, useLocation } from "react-router-dom";
import Navbar from "./components/Navbar";
import Preloader from "./components/Preloader";
import useMusicPlayer from "./hooks/useMusicPlayer";

import Home from "./pages/Home";
import PostersPage from "./pages/PostersPage";
import LogosPage from "./pages/LogosPage";
import VideosPage from "./pages/VideosPage";

import "./App.css";

const PRELOADER_MIN_MS = 1800;
const PRELOADER_EXIT_MS = 920;

function App() {
  const location = useLocation();
  const [isPreloaderVisible, setIsPreloaderVisible] = useState(true);
  const [isPreloaderMounted, setIsPreloaderMounted] = useState(true);

  useEffect(() => {
    if (location.pathname === "/") {
      return;
    }

    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);

  useEffect(() => {
    let hideTimer = null;
    let unmountTimer = null;
    const startTime = performance.now();

    const hidePreloader = () => {
      const elapsed = performance.now() - startTime;
      const waitTime = Math.max(0, PRELOADER_MIN_MS - elapsed);

      hideTimer = window.setTimeout(() => {
        setIsPreloaderVisible(false);
        unmountTimer = window.setTimeout(() => {
          setIsPreloaderMounted(false);
        }, PRELOADER_EXIT_MS);
      }, waitTime);
    };

    if (document.readyState === "complete") {
      hidePreloader();
    } else {
      window.addEventListener("load", hidePreloader, { once: true });
    }

    return () => {
      window.removeEventListener("load", hidePreloader);
      if (hideTimer) window.clearTimeout(hideTimer);
      if (unmountTimer) window.clearTimeout(unmountTimer);
    };
  }, []);

  useEffect(() => {
    if (!isPreloaderMounted) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isPreloaderMounted]);

  const {
    audioRef,
    audioSrc,
    handleAudioPause,
    handleAudioPlay,
    hasPlayableSong,
    isPlaying,
    song,
    togglePlayback,
    waveform,
  } = useMusicPlayer();

  return (
    <div className="app">
      {isPreloaderMounted ? <Preloader visible={isPreloaderVisible} /> : null}

      <Navbar
        hasPlayableSong={hasPlayableSong}
        isPlaying={isPlaying}
        onTogglePlayback={togglePlayback}
        song={song}
        waveform={waveform}
      />

      <Routes>
        <Route path="/admin" element={<Navigate to="/" replace />} />
        <Route path="/admin-dashboard" element={<Navigate to="/" replace />} />
        <Route
          path="/"
          element={
            <Home
              hasPlayableSong={hasPlayableSong}
              isPlaying={isPlaying}
              togglePlayback={togglePlayback}
              song={song}
              waveform={waveform}
            />
          }
        />
        <Route path="/works/posters" element={<PostersPage />} />
        <Route path="/works/logos" element={<LogosPage />} />
        <Route path="/works/videos" element={<VideosPage />} />
      </Routes>

      {audioSrc && (
        <audio
          ref={audioRef}
          src={audioSrc}
          crossOrigin="anonymous"
          loop
          preload="metadata"
          onPause={handleAudioPause}
          onPlay={handleAudioPlay}
        />
      )}
    </div>
  );
}

export default App;
